/**
 * @fileoverview useRovingTabIndex — keyboard focus management for grid/list layouts.
 *
 * Implements the WAI-ARIA roving-tabindex pattern: only one item in the
 * collection is in the tab sequence at a time, and arrow keys move focus
 * between siblings. Used by the Search empty state vibe pill grid (FRE-767).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnimationFrameCallback } from './useAnimationFrameCallback';
/**
 * Manages roving tabindex + arrow-key focus navigation across a grid.
 *
 * Wrap-around at edges: ArrowRight on the last column moves to the first
 * column of the next row; ArrowDown on the last row moves to the first row.
 */
export function useRovingTabIndex({ count, columns = 1, initialIndex = 0, }) {
    const safeCount = Math.max(0, count);
    const safeColumns = Math.max(1, columns);
    const safeInitial = safeCount > 0 ? Math.min(Math.max(0, initialIndex), safeCount - 1) : 0;
    const [activeIndex, setActiveIndex] = useState(safeInitial);
    const refs = useRef(new Map());
    // Clamp activeIndex when the collection shrinks so exactly one item always
    // owns `tabIndex={0}` and we never end up with a stale, out-of-bounds index.
    useEffect(() => {
        if (safeCount === 0) {
            if (activeIndex !== 0)
                setActiveIndex(0);
            return;
        }
        if (activeIndex > safeCount - 1) {
            setActiveIndex(safeCount - 1);
        }
    }, [activeIndex, safeCount]);
    const scheduleFrame = useAnimationFrameCallback();
    const focusIndex = useCallback((next) => {
        setActiveIndex(next);
        // Defer focus call so React commits state before we touch the DOM.
        scheduleFrame(() => {
            refs.current.get(next)?.focus();
        });
    }, [scheduleFrame]);
    const onKeyDown = useCallback((event, index) => {
        if (safeCount === 0)
            return;
        let next;
        switch (event.key) {
            case 'ArrowRight':
                next = (index + 1) % safeCount;
                break;
            case 'ArrowLeft':
                next = (index - 1 + safeCount) % safeCount;
                break;
            case 'ArrowDown': {
                const candidate = index + safeColumns;
                next = candidate < safeCount ? candidate : index % safeColumns;
                break;
            }
            case 'ArrowUp': {
                const candidate = index - safeColumns;
                if (candidate >= 0) {
                    next = candidate;
                }
                else {
                    const lastRowStart = Math.floor((safeCount - 1) / safeColumns) * safeColumns;
                    const candidateInLastRow = lastRowStart + (index % safeColumns);
                    next = candidateInLastRow < safeCount ? candidateInLastRow : safeCount - 1;
                }
                break;
            }
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = safeCount - 1;
                break;
            default:
                return;
        }
        event.preventDefault();
        if (next !== index) {
            focusIndex(next);
        }
    }, [focusIndex, safeColumns, safeCount]);
    const getTabIndex = useCallback((index) => (index === activeIndex ? 0 : -1), [activeIndex]);
    const registerRef = useCallback((index) => (node) => {
        if (node) {
            refs.current.set(index, node);
        }
        else {
            refs.current.delete(index);
        }
    }, []);
    return { activeIndex, getTabIndex, onKeyDown, registerRef };
}
